import { useQuery, useMutation } from "@tanstack/react-query";
import { api, buildUrl, type CreateSubmissionRequest } from "@shared/routes";
import { z } from "zod";

export function useGuestData(slug: string, token: string | null) {
  return useQuery({
    queryKey: [api.guest.getApartment.path, slug, token],
    queryFn: async () => {
      if (!token) throw new Error("Token required");
      const url = buildUrl(api.guest.getApartment.path, { slug }) + `?t=${token}`;
      const res = await fetch(url);
      if (res.status === 404) throw new Error("Apartment not found");
      if (res.status === 403) throw new Error("Invalid token");
      if (!res.ok) throw new Error("Failed to fetch guest data");
      return api.guest.getApartment.responses[200].parse(await res.json());
    },
    enabled: !!slug && !!token,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useSubmitCheckout() {
  return useMutation({
    mutationFn: async ({ slug, data }: { slug: string; data: z.infer<typeof api.guest.submitCheckout.input> }) => {
      const url = buildUrl(api.guest.submitCheckout.path, { slug });
      const validated = api.guest.submitCheckout.input.parse(data);
      
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(validated),
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to submit checkout");
      }
      return api.guest.submitCheckout.responses[201].parse(await res.json());
    },
  });
}
